/**
 * TestSelector — brand filter + search + card grid of test kits.
 *
 * Verbatin port of the standalone with additions for Bonus C (Due Soon badge)
 * and Bonus F (reagent-date editor entry-point).
 */
import { useState, useMemo } from 'react';
import type { TestKit, TestLogEntry } from '@reefapp/shared';
import { ALL_TEST_KITS } from '@reefapp/shared';
import { TestReminderBadge } from './TestReminderBadge';
import { ReagentDateEditor } from './ReagentDateEditor';
import type { ReminderSettings } from './useReminderSettings';
import type { ReagentDates } from './useReagentDates';
import { IconSearch } from './icons';

const STORAGE_KEY_BRAND = 'reefapp:test-kits:brand-filter';
const STORAGE_KEY_PARAM = 'reefapp:test-kits:parameter-filter';

function readSessionStorage(key: string, fallback: string): string {
  try {
    return sessionStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeSessionStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

export interface TestSelectorProps {
  testKits: Readonly<Record<string, ReadonlyArray<TestKit>>>;
  logs: ReadonlyArray<TestLogEntry>;
  reminderSettings: ReminderSettings;
  reagentDates: ReagentDates;
  monthsSince(slug: string): number | null;
  onSelectTest(kit: TestKit): void;
  onSetReagentDate(slug: string, iso: string | null): void;
}

export function TestSelector({
  testKits,
  logs,
  reminderSettings,
  reagentDates,
  monthsSince,
  onSelectTest,
  onSetReagentDate,
}: TestSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(
    () => readSessionStorage(STORAGE_KEY_BRAND, 'All'),
  );
  const [selectedParameter, setSelectedParameter] = useState(
    () => readSessionStorage(STORAGE_KEY_PARAM, 'All'),
  );
  const [editingReagent, setEditingReagent] = useState<string | null>(null);

  const brands = useMemo(
    () => ['All', ...Object.keys(testKits).sort()],
    [testKits],
  );

  /** Collect all unique parameter strings from the flat kit list. */
  const parameters = useMemo(() => {
    const set = new Set<string>();
    for (const kit of ALL_TEST_KITS) {
      set.add(kit.parameter);
    }
    return ['All', ...[...set].sort()];
  }, []);

  const filteredKits = useMemo(() => {
    const lowerCaseSearch = searchTerm.toLowerCase();

    return Object.entries(testKits)
      .filter(([brand]) => selectedBrand === 'All' || brand === selectedBrand)
      .map(([brand, kits]) => {
        const filtered = kits.filter(
          (kit) => {
            if (selectedParameter !== 'All' && kit.parameter !== selectedParameter) {
              return false;
            }
            if (!lowerCaseSearch) return true;
            return (
              kit.name.toLowerCase().includes(lowerCaseSearch) ||
              kit.parameter.toLowerCase().includes(lowerCaseSearch) ||
              kit.description.toLowerCase().includes(lowerCaseSearch)
            );
          },
        );
        return { brand, kits: filtered };
      })
      .filter((group) => group.kits.length > 0);
  }, [searchTerm, selectedBrand, selectedParameter, testKits]);

  return (
    <div className="space-y-6">
      {/* Sticky search + filter bar */}
      <div className="sticky top-0 bg-slate-950/80 backdrop-blur-sm z-10 py-4 -my-4">
        <div className="relative mb-4">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <IconSearch className="h-5 w-5 text-slate-500" />
          </div>
          <input
            type="search"
            placeholder="Search by name, parameter, or keyword..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-slate-700 bg-slate-800 py-3 pl-10 pr-4 text-slate-200 shadow-sm placeholder:text-slate-500 focus:border-cyan-500 focus:ring-cyan-500"
          />
        </div>
        <div className="flex flex-wrap gap-2 justify-center mb-2">
          {brands.map((brand) => (
            <button
              key={brand}
              onClick={() => {
                setSelectedBrand(brand);
                writeSessionStorage(STORAGE_KEY_BRAND, brand);
              }}
              className={`px-4 py-2 text-sm font-semibold border rounded-full shadow-sm transition-all duration-200 ${
                selectedBrand === brand
                  ? 'bg-cyan-600 text-white border-cyan-600'
                  : 'bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700 hover:border-slate-500'
              }`}
            >
              {brand}
            </button>
          ))}
        </div>

        {/* Parameter filter dropdown */}
        <div className="flex justify-center">
          <select
            value={selectedParameter}
            onChange={(e) => {
              setSelectedParameter(e.target.value);
              writeSessionStorage(STORAGE_KEY_PARAM, e.target.value);
            }}
            className="block rounded-md border-slate-700 bg-slate-800 py-2 px-3 text-sm text-slate-200 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
            aria-label="Filter by parameter"
          >
            {parameters.map((p) => (
              <option key={p} value={p}>
                {p === 'All' ? 'All parameters' : p}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Card grid */}
      {filteredKits.length > 0 ? (
        filteredKits.map(({ brand, kits }) => (
          <div key={brand}>
            <h2 className="text-2xl font-bold text-slate-200 mb-4 pb-2 border-b-2 border-cyan-800">
              {brand}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {kits.map((kit) => {
                const reagentMonths = monthsSince(kit.slug);
                const isExpired =
                  reagentMonths !== null && reagentMonths > 12;
                const expiredMonths =
                  isExpired && reagentMonths !== null
                    ? Math.floor(reagentMonths)
                    : 0;
                const reminderSetting = reminderSettings[kit.parameterKey];
                const reminderEnabled = reminderSetting?.enabled ?? false;
                const reminderDays = reminderSetting?.thresholdDays ?? 7;

                return (
                  <div
                    key={`${kit.brand}-${kit.name}`}
                    className="bg-slate-800 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col h-full border border-slate-700 relative"
                  >
                    {/* Reagent info button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingReagent(
                          editingReagent === kit.slug ? null : kit.slug,
                        );
                      }}
                      className="absolute top-3 right-3 text-slate-500 hover:text-cyan-400 text-lg leading-none"
                      title="Reagent opened date"
                    >
                      &#9432;
                    </button>

                    {editingReagent === kit.slug && (
                      <ReagentDateEditor
                        kitSlug={kit.slug}
                        currentDate={reagentDates[kit.slug]}
                        onChange={(iso) => {
                          onSetReagentDate(kit.slug, iso);
                          setEditingReagent(null);
                        }}
                      />
                    )}

                    <div className="p-5 flex-grow">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-bold text-cyan-500">
                          {kit.parameter}
                        </p>
                        {reminderEnabled && (
                          <TestReminderBadge
                            parameterKey={kit.parameterKey}
                            logs={logs}
                            enabled={reminderEnabled}
                            thresholdDays={reminderDays}
                          />
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-slate-100 mb-2">
                        {kit.name}
                      </h3>
                      <p className="text-slate-400 text-sm leading-relaxed line-clamp-3">
                        {kit.description}
                      </p>
                      {isExpired && (
                        <p className="mt-2 text-xs text-yellow-400 font-medium">
                          &#9888; Reagents may be expired ({expiredMonths} mo
                          old)
                        </p>
                      )}
                    </div>
                    <div className="bg-slate-750 p-4 rounded-b-lg mt-auto">
                      <button
                        onClick={() => onSelectTest(kit)}
                        className="w-full bg-cyan-600 text-white font-bold py-2 px-4 rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 focus:ring-cyan-500 transition-colors"
                      >
                        Perform Test
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-16 px-6 bg-slate-800 rounded-lg shadow-md border border-slate-700">
          <IconSearch className="w-16 h-16 mx-auto text-slate-600" />
          <h3 className="mt-4 text-xl font-semibold text-slate-300">
            No kits match your filters.
          </h3>
          <p className="mt-2 text-slate-500">
            Try adjusting your search, brand, or parameter filter.
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedBrand('All');
              writeSessionStorage(STORAGE_KEY_BRAND, 'All');
              setSelectedParameter('All');
              writeSessionStorage(STORAGE_KEY_PARAM, 'All');
            }}
            className="mt-4 px-4 py-2 text-sm font-semibold text-slate-300 bg-slate-700 rounded-md hover:bg-slate-600 transition-colors"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}
